import { type TFunction } from 'i18next'

import { type TranslationSchema } from '~/locales/resources'

import { type TranslatableFigureInfoKey } from '../figureInfo'

import { normalizeValueToKey } from './normalizeValueToKey'

// キーから i18n キーへのマッピング
const KEY_TO_I18N_MAP: Record<string, keyof TranslationSchema> = {
  // dataDisplayType
  percentage: 'percentage',
  sample_size: 'sample_size',
  estimated_population: 'estimated_population',
  // segmentDisplayType
  five_segs: 'five_segs',
  nine_segs: 'nine_segs',
  both: 'both',
  summary: 'summary',
  // resultViewerType
  show_tables_graphs: 'show_tables_graphs',
  show_table_only: 'show_table_only',
  // tableChartType
  graph: 'graph',
  table: 'table',
}

// figureType 用のマッピング
const FIGURE_TYPE_KEY_TO_I18N_MAP: Record<string, keyof TranslationSchema> = {
  // カスタマイズ分析
  individual_questionnaire: 'individual_questionnaire',
  group_matrix: 'group_matrix',
  continuous_questionnaire: 'continuous_questionnaire',
  // 時系列分析
  '9segs_customer_distribution': '9segs_customer_distribution',
  aware: 'aware',
  existing_customer: 'existing_customer',
  loyal_customer: 'loyal_customer',
  user_conversion_rateamong_aware: 'user_conversion_rateamong_aware',
  loyal_customersty_rateamong_existing_customers:
    'loyal_customersty_rateamong_existing_customers',
  npi: 'npi',
  aNpi: 'aNpi',
  nNpi: 'nNpi',
  uNpi: 'uNpi',
  // tableChartType（時系列分析の複合キーで使用）
  graph: 'graph',
  table: 'table',
}

// screenLabel 用のマッピング
const SCREEN_LABEL_KEY_TO_I18N_MAP: Record<string, keyof TranslationSchema> = {
  // pathGroupLabel
  deep_dive_analysis: 'deep_dive_analysis',
  core_analysis: 'core_analysis',
  time_series: 'time_series',
  customer_strategy: 'customer_strategy',
  brand_account_settings: 'brand_account_settings',
  documents_1: 'documents_1',
  survey_settings: 'survey_settings',
  // pathLabel
  '9segs_flexible_mode': '9segs_flexible_mode',
  '9segs_customer_data': '9segs_customer_data',
  '9segs_customer_distribution': '9segs_customer_distribution',
  overlap_analysis: 'overlap_analysis',
  customized_segments_analysis: 'customized_segments_analysis',
  time_series_analysis: 'time_series_analysis',
}

/**
 * 英語キーを現在の言語で翻訳します。
 *
 * @param figureInfoKey - 翻訳対象のキー
 * @param value - 英語キーまたは翻訳済み値
 * @param t - i18next の翻訳関数
 * @returns 翻訳された値
 */
export function translateFigureInfoValue(
  figureInfoKey: TranslatableFigureInfoKey,
  value: string,
  t: TFunction,
): string {
  // まずキーに正規化
  const key = normalizeValueToKey(figureInfoKey, value)

  switch (figureInfoKey) {
    case 'dataDisplayType':
    case 'segmentDisplayType':
    case 'resultViewerType':
    case 'tableChartType': {
      const i18nKey = KEY_TO_I18N_MAP[key]

      return i18nKey ? t(i18nKey) : value
    }
    case 'figureType':
      // " - " で区切られた複合キー（例: existing_customer - graph）を個別に翻訳
      return key
        .split(' - ')
        .map((part) => {
          const i18nKey = FIGURE_TYPE_KEY_TO_I18N_MAP[part]

          return i18nKey ? t(i18nKey) : part
        })
        .join(' - ')
    case 'screenLabel':
      // " - " で区切られた複合キーなので個別に翻訳
      return key
        .split(' - ')
        .map((part) => {
          const i18nKey = SCREEN_LABEL_KEY_TO_I18N_MAP[part]

          return i18nKey ? t(i18nKey) : part
        })
        .join(' - ')
    default:
      return value
  }
}
